import { DragEndEvent, DndContext, useDroppable, useDraggable } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import React = require("react");
import styled from "styled-components";
import { Survey, LanguageCode, getSurveyStatus, SurveySegmentation, Segmentation } from "../../../../../../../amorg-common";
import { updateSurveySegmentation } from "../../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../../designSystem/button/Button";
import { Card } from "../../../../../../../designSystem/Card";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { DragButton } from "../../../../../../../designSystem/dragndrop/DragButton";
import { Icon } from "../../../../../../../designSystem/icons/Icons";
import { InfoChip } from "../../../../../../../designSystem/Chip";


type SegmentationListProps = {
    survey: Survey,
    selectedLanguage:LanguageCode,
    onSelectForEdit: (segmentationIndex: number) => void,
    onSurveyUpdated: () => void,
}
export function SegmentationList(props: SegmentationListProps): JSX.Element {
    const disableEdit = !getSurveyStatus(props.survey).canEdit;

    function getSelectedSegmentation(seg:SurveySegmentation):Segmentation
    {
        if(props.selectedLanguage===props.survey.languageCode)
            return {name:seg.name, values:seg.values};
        
        const trans = seg.translations.find(t => t.languageCode===props.selectedLanguage);
        if(!trans) throw new Error('Missing translation');
        return { name:trans.name, values:trans.values };
    }


    async function handleMoveSegment(move: 'up' | 'down', segIndex) {

        if (move === 'up') {
            if (segIndex > 0) {
                await updateSurveySegmentation(props.survey.id, segIndex, segIndex-1, props.survey.segmentations[segIndex]);
                props.onSurveyUpdated();
            }
        }
        if (move === 'down') {
            if (segIndex < props.survey.segmentations.length - 1) {
                await updateSurveySegmentation(props.survey.id, segIndex, segIndex+1, props.survey.segmentations[segIndex]);
                props.onSurveyUpdated();
            }
        }
    }

    async function handleDragEnd(event: DragEndEvent) {
        if (disableEdit) {
            return;
        }

        const activeId = event.active.id.toString();
        const overId = event.over?.id.toString();

        if (!overId || activeId === overId) {
            return;
        }

        if (!activeId.startsWith('segmentation_') || !overId.startsWith('segmentation_')) {
            return;
        }

        const fromIndex = parseInt(activeId.substring(13));
        const toIndex = parseInt(overId.substring(13));

        if (fromIndex === toIndex) {
            return;
        }

        const result = await updateSurveySegmentation(
            props.survey.id,
            fromIndex,
            toIndex,
            props.survey.segmentations[fromIndex]
        );

        if (result.isSuccess) {
            props.onSurveyUpdated();
        }
    }

    return (
        <>
            <DndContext onDragEnd={handleDragEnd}>
                <SegmentationGrid>
                    {props.survey.segmentations.map((c, i) => {

                        const seg = getSelectedSegmentation(c);

                        return (
                            <SegmentationListItem
                                key={seg.name}
                                index={i}
                                seg={seg}
                                disableEdit={disableEdit}
                                onSelectForEdit={props.onSelectForEdit}
                                onMoveSegment={handleMoveSegment}
                            />
                        );
                    })}
                </SegmentationGrid>
            </DndContext>
        

            {props.survey.segmentations.length === 0 &&
                <p>
                    Der er ikke oprettet nogen segmenteringer. Med segmenteringer kan du inddele respondenter efter arbejdssted, afdeling, aldersgruppe mm.
                </p>
            }
        </>
    );
}

type SegmentationListItemProps = {
    index: number,
    seg: Segmentation,
    disableEdit: boolean,
    onSelectForEdit: (segmentationIndex: number) => void,
    onMoveSegment: (move: 'up' | 'down', segIndex: number) => void,
}

function SegmentationListItem(props: SegmentationListItemProps): JSX.Element {
    const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
        id: `segmentation_${props.index}`,
    });

    const { attributes, listeners, setNodeRef: setDraggableNodeRef, setActivatorNodeRef, transform } = useDraggable({
        id: `segmentation_${props.index}`,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    return (
        <Card variant="outlined">
        <div
            ref={(node) => {
                setDroppableNodeRef(node);
                setDraggableNodeRef(node);
            }}
            {...attributes}
            style={{
                ...draggableStyle,
                backgroundColor: isOver ? '#f0f0f0' : undefined,
            }}>
                <Flex alignItems="center" gap={8}>
                    <div>
                        {!props.disableEdit &&
                            <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />
                        }
                    </div>
                    <Flex flexDirection="column" gap={8} grow="1">
                        <Flex justifyContent="flex-start" alignItems="center" style={{border: '0px solid #ccc', minHeight: '32px'}}>
                            <InfoChip colorHash={props.seg.name}>
                                {props.seg.name}
                            </InfoChip>
                        </Flex>
                        <Flex alignItems="center" gap={4} style={{flexWrap:'wrap'}}>
                            <Icon name="list" size="x-small" />
                            <i>{props.seg.values.length} mulige værdier</i>
                        </Flex>
                    </Flex>
                    <Button variant="text" icon="interface_actions_edit" onClick={() => props.onSelectForEdit(props.index)} disabled={props.disableEdit} />
                </Flex>
            </div>
        </Card>
    );
}

const SegmentationGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 33.3%);
    gap: 4px;
    align-items: center;
`;